import * as React from "react"

import { Ellipsis } from "lucide-react"

import { cn } from "../../cn"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../DropdownMenu/DropdownMenu"

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Renders the card as a clickable control: adds pointer/hover/focus
   * affordances, `role="button"`, keyboard activation (Enter/Space), and a
   * focus ring. Pair with an `onClick` handler.
   */
  interactive?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive, onClick, onKeyDown, ...props }, ref) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event)
      // Only activate when the card itself is focused, not a nested control.
      if (event.target !== event.currentTarget) {
        return
      }
      if (
        interactive &&
        !event.defaultPrevented &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault()
        event.currentTarget.click()
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          "group rounded-md border border-coval-border bg-surface-default text-sm text-coval-text",
          interactive &&
            "cursor-pointer transition-colors hover:bg-surface-default-hover hover:border-coval-border-active focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active",
          className,
        )}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...(interactive ? { role: "button", tabIndex: 0 } : {})}
        {...props}
      />
    )
  },
)

Card.displayName = "Card"

export interface CardOverflowMenuItem {
  label: string
  onSelect: () => void
  destructive?: boolean
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode
  onIconClick?: () => void
  iconLabel?: string
  overflowMenu?: CardOverflowMenuItem[]
  overflowLabel?: string
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  (
    {
      className,
      children,
      icon,
      onIconClick,
      iconLabel = "Action",
      overflowMenu,
      overflowLabel = "More options",
      ...props
    },
    ref,
  ) => {
    const hasActions = icon || overflowMenu
    const [menuOpen, setMenuOpen] = React.useState(false)

    return (
      <div ref={ref} className={cn("flex gap-1 p-4", className)} {...props}>
        <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
        {hasActions && (
          <div
            className={cn(
              "flex shrink-0 items-start gap-0.5 transition-opacity duration-150",
              menuOpen
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
            )}
          >
            {icon && (
              <button
                type="button"
                onClick={onIconClick}
                aria-label={iconLabel}
                className={cn(
                  "inline-flex size-7 items-center justify-center rounded-sm text-coval-text-muted",
                  "hover:bg-surface-default-hover hover:text-coval-text",
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active",
                  "transition-colors duration-150",
                )}
              >
                {icon}
              </button>
            )}
            {overflowMenu && overflowMenu.length > 0 && (
              <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={overflowLabel}
                    className={cn(
                      "inline-flex size-7 items-center justify-center rounded-sm text-coval-text-muted",
                      "hover:bg-surface-default-hover hover:text-coval-text",
                      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active",
                      "transition-colors duration-150",
                    )}
                  >
                    <Ellipsis className="size-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {overflowMenu.map((item) => (
                    <DropdownMenuItem
                      key={item.label}
                      onSelect={item.onSelect}
                      destructive={item.destructive}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </div>
    )
  },
)

CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("text-sm font-semibold text-coval-text", className)}
    {...props}
  />
))

CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-coval-text-muted", className)}
    {...props}
  />
))

CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-4 pb-4", className)} {...props} />
))

CardContent.displayName = "CardContent"

export { Card, CardHeader, CardTitle, CardDescription, CardContent }
